import type {PhotoPick} from '@/lib/photo-capture/image-input';
import type {PhotoRequirement} from '../../lib/types';
import type {InlineEditState} from '../InlineLabel';

/**
 * The contract every element renderer is called with — and the fill plumbing
 * behind it.
 *
 * One shape, so `PreviewElement` can dispatch on `item.type` and hand each
 * renderer the same props; what varies is which of them a given renderer reads.
 */

export interface ElemProps {
  label: string;
  fillMode: boolean;
  inlineEdit?: InlineEditState;
  /**
   * Interactive-fill plumbing. When `fillMode` is on AND `onFillChange` is
   * provided, the input renderers become controlled and capture the worker's
   * answer (the value lives in `fillValue`). When absent — the editor's own
   * preview toggle — the renderers fall back to the read-only/sample preview.
   */
  fillValue?: unknown;
  onFillChange?: (value: unknown) => void;
  /**
   * A photo was picked for this slot.
   *
   * `aspect` is the slot's RENDERED width ÷ height, measured off the box the
   * photographer just tapped. The crop frame is built from it, and the config
   * cannot answer for it: an in-cell image input with no width stretches to its
   * column, so its shape is not written down anywhere until it is on screen.
   * `undefined` when the box could not be measured.
   */
  onFillPhoto?: (pick: PhotoPick, aspect?: number) => void;
  /**
   * Read-only fill: show the captured `fillValue` but disable editing. Used by
   * the completed-job report view.
   */
  fillReadOnly?: boolean;
  /**
   * This slot is the OTHER half's, on a document still being filled — not the
   * same thing as `fillReadOnly`, which means the document is settled.
   *
   * A renderer draws the control either way: the box is where the other party
   * will type, and it is part of the document whoever is looking at it. Only
   * `fillReadOnly` turns a field into a finished line to be read. Collapsing the
   * two is what made one JAC paginate differently on the job and on the client's
   * link — see `fillProps`.
   */
  fillLocked?: boolean;
  // Who is looking, and the signer defaults, are NOT here: they are constant
  // across a surface rather than per element, so they come from
  // `FillSurface` instead of being handed to every renderer.
  /** Variable context for {{token}} resolution in placeholders / defaults. */
  sampleData?: Record<string, string>;
  /** Design-mode: update this element's config (e.g. pick the logo to stamp). */
  onChangeElement?: (updated: PhotoRequirement) => void;
  /**
   * Take the full width of whatever contains this element, ignoring the
   * configured / default one.
   *
   * Set only by `CellElementFill`, and only for a cell element the author never
   * gave an explicit width. A standalone element sits on a page and its size is
   * its own business; one in a table cell is sized by the COLUMN, which the
   * author drags. The editor has always drawn it that way, so a renderer that
   * substitutes a fixed default here draws a different document from the one
   * being designed.
   */
  spanWidth?: boolean;
  /**
   * The cell's own typography, for a control inside one to render its text in.
   *
   * A cell element's `style` is empty by design — a cell's type and borders come
   * from the `<td>`, and letting the element restate them would double-apply —
   * but a form control does not inherit type from an ancestor: a browser gives
   * it its own, and Tailwind then names a size on top. So a cell set to 14pt
   * bold navy drew its heading that way and the input beside it at 12pt grey,
   * and the author's setting silently did nothing to the thing they had selected.
   * Passed as the built CSS rather than the raw cell style, so `buildCellStyle`
   * stays the one place that decides what a cell's text looks like.
   */
  textStyle?: React.CSSProperties;
}

/**
 * The per-slot fill plumbing, scoped to one id.
 *
 * Most elements are handed the RESULT of this for their own id. A CONTAINER
 * element — today only `table` (DERP-225) — is handed the function instead: one
 * table holds many independent slots, so it calls this per cell with that cell's
 * `cellFillKey`. Both paths therefore run through the same scoping in
 * `LivePreview`, and answers land in one flat namespace, so the report row's
 * `answers`, `checkout_data`'s mirror and photo-attachment tagging all work
 * unchanged. (It said "the draft store" — that was an in-memory client store,
 * replaced by the server-side row in DERP-204.)
 */
export type FillSlotProps = (
  id: string,
) => Pick<ElemProps, 'fillValue' | 'onFillChange' | 'onFillPhoto' | 'fillReadOnly'>;

/** Captured signature value: drawn image + chosen signer + capture time. */
export interface SignatureValue {
  image: string;
  signer_name?: string;
  signed_at?: string;
}

export function normalizeSignature(v: unknown): SignatureValue {
  if (typeof v === 'string') return {image: v};
  if (v && typeof v === 'object') return v as SignatureValue;
  return {image: ''};
}
